import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Bot,
  CheckCheck,
  FileText,
  MessageCircle,
  Plus,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { WhatsAppHeader } from "@/components/whatsapp/WhatsAppWorkspace";
import { Card, SectionHeader } from "@/components/Card";
import { Button } from "@/components/Button";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useActiveAccount } from "@/hooks/useActiveAccount";
import {
  AccountActivityMap,
  AccountStatsMap,
  whatsappAccountService,
} from "@/services/whatsapp-account.service";
import {
  WhatsappAsset,
  whatsappPlatformService,
} from "@/services/whatsapp-platform.service";
import clsx from "clsx";

const number = (n: number) => n.toLocaleString();
export default function WhatsappOverview() {
  const { accounts, active, loading: accountsLoading } = useActiveAccount();
  const [selected, setSelected] = useState("");
  const [days, setDays] = useState(30);
  const [activity, setActivity] = useState<AccountActivityMap>({});
  const [stats, setStats] = useState<AccountStatsMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [meta, setMeta] = useState<WhatsappAsset | null>(null);
  const [metaBusy, setMetaBusy] = useState(false);
  const [metaError, setMetaError] = useState("");
  const account =
    accounts.find((a) => a.accountUid === selected) ?? active ?? accounts[0];
  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [a, s] = await Promise.all([
        whatsappAccountService.activity(days),
        whatsappAccountService.stats(),
      ]);
      setActivity(a);
      setStats(s);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [days]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    setMeta(null);
    setMetaError("");
  }, [account?.accountUid, days]);
  const daily = useMemo(() => account ? (activity[account.accountUid]?.daily ?? []) : [], [account, activity]);
  const totals = useMemo(
    () =>
      daily.reduce(
        (sum, day) => ({
          inbound: sum.inbound + day.inbound,
          outbound: sum.outbound + day.outbound,
        }),
        { inbound: 0, outbound: 0 },
      ),
    [daily],
  );
  const receipt = account
    ? (stats[account.accountUid] ?? {
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0,
      })
    : { sent: 0, delivered: 0, read: 0, failed: 0 };
  const templateCount = account
    ? (activity[account.accountUid]?.templates.total ?? 0)
    : 0;
  const chart = daily.map((d) => ({
    ...d,
    label: new Date(`${d.date}T00:00:00Z`).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }),
  }));
  const refreshMeta = async () => {
    if (!account) return;
    setMetaBusy(true);
    setMetaError("");
    try {
      setMeta(
        await whatsappPlatformService.analytics(account.accountUid, days, true),
      );
    } catch (e: any) {
      setMetaError(
        e?.response?.data?.message ?? "Meta analytics could not be loaded.",
      );
    } finally {
      setMetaBusy(false);
    }
  };
  const metaDays = (meta?.data.analytics?.data_points ?? []).map((d: any) => ({
    date: new Date(d.start * 1000).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    }),
    sent: d.sent,
    delivered: d.delivered,
  }));
  return (
    <>
      <WhatsAppHeader
        title="Overview"
        section="overview"
        description="Every conversation, campaign and business number. One connected workspace."
        actions={
          <>
            <Button
              variant="secondary"
              onClick={load}
              loading={loading}
              iconLeft={<RefreshCw size={15} />}
            >
              Refresh
            </Button>
            <Link to="/campaigns/new">
              <Button iconLeft={<Plus size={16} />}>Create campaign</Button>
            </Link>
          </>
        }
      />
      <div className="space-y-6 p-4 sm:p-7">
        {error && (
          <div
            role="alert"
            className="rounded-xl border border-danger/25 bg-danger-soft p-4 text-sm text-danger"
          >
            Could not load message analytics. Refresh to try again.
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-surface p-5">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-primary-soft text-primary">
              {account?.profilePicUrl ? (
                <img
                  src={account.profilePicUrl}
                  alt="Business profile"
                  className="h-full w-full object-cover"
                />
              ) : (
                <MessageCircle size={25} />
              )}
            </div>
            <div>
              <h2 className="font-semibold">
                {account?.businessName ??
                  (accountsLoading
                    ? "Loading accounts…"
                    : "Connect your business")}
              </h2>
              <p className="mt-1 text-xs text-text-muted">
                {account
                  ? `${account.displayPhoneNumber} · ${account.verifiedName}`
                  : "Your WhatsApp workspace starts with a business number."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {accounts.length > 1 && (
              <div className="w-56">
                <SearchableSelect
                  aria-label="Reporting account"
                  value={account?.accountUid ?? ""}
                  onChange={setSelected}
                  options={accounts.map((a) => ({ value: a.accountUid, label: a.businessName, description: a.displayPhoneNumber }))}
                  numbered={false}
                  searchable={accounts.length > 6}
                />
              </div>
            )}
            <Link
              to="/whatsapp-accounts"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary"
            >
              Manage account <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Messaging performance</h2>
            <p className="mt-1 text-xs text-text-muted">
              Messages recorded in CRM · daily totals in UTC
            </p>
          </div>
          <div className="w-40">
            <SearchableSelect
              aria-label="Reporting period"
              value={String(days)}
              onChange={(v) => setDays(Number(v))}
              options={[{ value: "7", label: "Last 7 days" }, { value: "30", label: "Last 30 days" }, { value: "90", label: "Last 90 days" }]}
              numbered={false}
              searchable={false}
            />
          </div>
        </div>
        <div className="grid overflow-hidden rounded-2xl border border-border bg-surface sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              label: "Messages sent",
              value: totals.outbound,
              detail: `Last ${days} days`,
              icon: ArrowUpRight,
            },
            {
              label: "Customer messages",
              value: totals.inbound,
              detail: `Last ${days} days`,
              icon: ArrowDownLeft,
            },
            {
              label: "Delivered",
              value: receipt.delivered,
              detail: "All-time confirmed delivery",
              icon: CheckCheck,
            },
            {
              label: "Templates",
              value: templateCount,
              detail: "Drafts and submitted templates",
              icon: FileText,
            },
          ].map((m) => (
            <div
              key={m.label}
              className="border-b border-border p-5 last:border-0 sm:border-r xl:border-b-0"
            >
              <div className="flex justify-between text-sm text-text-muted">
                {m.label}
                <m.icon size={17} />
              </div>
              <div className="mt-5 text-3xl font-semibold tabular-nums tracking-tight">
                {loading ? "—" : number(m.value)}
              </div>
              <div className="mt-2 text-xs text-text-muted">{m.detail}</div>
            </div>
          ))}
        </div>
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
          <Card>
            <SectionHeader
              title="Conversations over time"
              subtitle="Inbound messages and accepted outbound messages"
            />
            <div className="h-72 w-full">
              {daily.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart} barGap={2}>
                    <CartesianGrid
                      vertical={false}
                      stroke="var(--color-border)"
                      strokeDasharray="3 3"
                    />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      minTickGap={30}
                      tick={{ fontSize: 11, fill: "var(--color-text-muted)" }}
                    />
                    <YAxis
                      allowDecimals={false}
                      axisLine={false}
                      tickLine={false}
                      width={38}
                      tick={{ fontSize: 11, fill: "var(--color-text-muted)" }}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid var(--color-border)",
                        background: "var(--color-surface)",
                      }}
                    />
                    <Legend
                      iconType="circle"
                      wrapperStyle={{ fontSize: 12, paddingTop: 16 }}
                    />
                    <Bar
                      dataKey="outbound"
                      name="Sent"
                      fill="#c7a451"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="inbound"
                      name="Received"
                      fill="#179c7b"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full flex-col items-center justify-center rounded-xl border border-dashed border-border bg-surface-alt/30 text-center">
                  <MessageCircle className="mb-3 text-primary" size={30} />
                  <p className="text-sm font-medium">
                    No recorded messages in this period
                  </p>
                  <p className="mt-2 text-xs text-text-muted">
                    New messages and campaign activity will appear here.
                  </p>
                </div>
              )}
            </div>
          </Card>
          <Card>
            <SectionHeader
              title="Account health"
              icon={<ShieldCheck size={19} />}
            />
            <div
              className={clsx(
                "mb-4 rounded-xl p-4",
                account?.qualityRating === "RED"
                  ? "bg-danger-soft"
                  : account?.qualityRating === "GREEN"
                    ? "bg-success-soft"
                    : "bg-surface-alt",
              )}
            >
              <div className="text-sm font-semibold">
                {account?.qualityRating === "GREEN"
                  ? "Good quality"
                  : account?.qualityRating === "YELLOW"
                    ? "Quality needs attention"
                    : account?.qualityRating === "RED"
                      ? "Low quality · sending at risk"
                      : "Quality not yet reported"}
              </div>
              <p className="mt-2 text-xs leading-5 text-text-muted">
                {account?.qualityRating === "RED" ||
                account?.qualityRating === "YELLOW"
                  ? "Reduce volume and review opt-ins. Blocks and reports can restrict or suspend this number."
                  : "Send to customers who expect your messages and honour every opt-out."}
              </p>
            </div>
            <dl className="space-y-4 text-sm">
              <div className="flex justify-between">
                <dt className="text-text-muted">Messaging tier</dt>
                <dd className="font-medium">
                  {account?.messagingLimitTier
                    ?.replace("TIER_", "")
                    .replace(/_/g, " ") ?? "Not reported"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-text-muted">Automation</dt>
                <dd className="font-medium">
                  {account?.botMode === "ai_chat"
                    ? "AI assistant"
                    : account?.botMode === "flow"
                      ? "Scripted flows"
                      : "Manual replies"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-text-muted">Failed messages</dt>
                <dd
                  className={
                    receipt.failed ? "font-medium text-danger" : "font-medium"
                  }
                >
                  {number(receipt.failed)}
                </dd>
              </div>
            </dl>
            <p className="mt-4 text-[11px] text-text-muted">
              Health{" "}
              {account?.healthFetchedAt
                ? `last checked ${new Date(account.healthFetchedAt).toLocaleString()}`
                : "has not been checked yet"}
              .
            </p>
            <Link
              to="/whatsapp-account/tools?tab=audit"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary"
            >
              View health & safeguards <ArrowUpRight size={15} />
            </Link>
          </Card>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            {
              title: "Create a conversation",
              detail: "Personalised templates, rich media and carousels.",
              to: "/templates/new",
              icon: FileText,
            },
            {
              title: "Turn conversations into customers",
              detail: "Build a bot or generate one with your company AI.",
              to: "/leads/bot-flows",
              icon: Bot,
            },
            {
              title: "Put your products in the chat",
              detail: "Connect your catalog, forms and WhatsApp entry points.",
              to: "/whatsapp/catalog",
              icon: ShoppingBag,
            },
          ].map((c) => (
            <Link key={c.to} to={c.to}>
              <Card hoverable className="h-full">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <c.icon size={20} />
                </div>
                <h3 className="text-sm font-semibold">{c.title}</h3>
                <p className="mt-2 text-xs leading-5 text-text-muted">
                  {c.detail}
                </p>
              </Card>
            </Link>
          ))}
        </div>
        <Card>
          <SectionHeader
            title="Meta delivery analytics"
            subtitle={`Official WhatsApp Business API v26 · last ${days} days`}
            action={
              <Button
                variant="secondary"
                disabled={!account}
                loading={metaBusy}
                onClick={refreshMeta}
                iconLeft={<RefreshCw size={14} />}
              >
                {meta ? "Sync again" : "Load from Meta"}
              </Button>
            }
          />
          {metaError ? (
            <p className="rounded-xl bg-warning-soft p-4 text-sm">
              {metaError}
            </p>
          ) : metaDays.length ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metaDays}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    minTickGap={28}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="sent"
                    fill="#c7a451"
                    name="Sent"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="delivered"
                    fill="#179c7b"
                    name="Delivered"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-text-muted">
              {meta
                ? "Meta has no reporting data for this number in this period."
                : "Load reporting from Meta to compare confirmed delivery with your CRM history."}
            </p>
          )}
        </Card>
      </div>
    </>
  );
}
